import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import logo from '../images/logo.svg';

function Home() {
  const [successMessage, setSuccessMessage] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <img src={logo} alt="Laftel Condo Logo" className="h-24 w-auto" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Laftel Condo
            </h1>
            <p className="text-lg text-muted-foreground mb-4">
              Automated Condo Management Platform currently in testing phase.
            </p>
            <p className="text-sm text-muted-foreground max-w-3xl mx-auto">
              Condo management is stuck in the past. Fragmented communication and manual processes drive delays and unnecessary costs. Laftel Condo brings clarity, automation, and shared ownership to deliver faster resolutions, lower fees, and more efficient communities.
            </p>
          </div>

          {successMessage && (
            <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-6 max-w-2xl mx-auto">
              {successMessage}
            </div>
          )}

          <div className="mb-16">
            <h2 className="text-3xl font-bold text-foreground text-center mb-8">Key Features</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="mb-4 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Automated Ticket Management</h3>
                <p className="text-muted-foreground">
                  Email-based issue tracking that automatically creates, updates, and organizes maintenance requests 
                  and building concerns. Track status, priority, and resolution history effortlessly.
                </p>
              </div>

              <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="mb-4 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Community Forum</h3>
                <p className="text-muted-foreground">
                  Connect with your neighbors through a dedicated community space. Share updates, ask questions, 
                  and engage in discussions about building matters in a friendly, organized environment.
                </p>
              </div>

              <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="mb-4 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Calendar</h3>
                <p className="text-muted-foreground">
                  Stay informed about upcoming building events, maintenance schedules, and important dates. 
                  View weekly or daily schedules with automatic event extraction from announcements.
                </p>
              </div>

              <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="mb-4 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Bulletins</h3>
                <p className="text-muted-foreground">
                  Receive important building-wide announcements and notices. Bulletins are automatically processed 
                  from management emails, keeping everyone up-to-date with minimal effort.
                </p>
              </div>

              <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="mb-4 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Handbook</h3>
                <p className="text-muted-foreground">
                  Access building rules, policies, and guidelines in one organized location. From parking regulations 
                  to move-in procedures, find answers to common questions quickly.
                </p>
              </div>

              <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="mb-4 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 10h.01M15 10h.01" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Amenities</h3>
                <p className="text-muted-foreground">
                  Book the gym, rooftop terrace, and other shared spaces in a few taps. See open time slots, 
                  follow each amenity's booking rules, and manage your upcoming bookings in one place.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-border mt-8">
        <div className="container mx-auto px-4 py-8 pb-20 md:pb-8 flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Laftel Condo logo" className="w-8 h-8" />
            <span className="text-foreground font-semibold text-lg">Laftel Condo</span>
          </div>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="hover:text-foreground transition-colors">
              Terms and Conditions
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
